// Player progress in one versioned localStorage record. Every read goes
// through normalizeSave, so a missing, old or hand-edited record can never
// crash the game — at worst the player starts over as a white belt.
// The record is only written once the player agreed (see loadConsent); until
// then progress lives in memory for the session.

import { BELTS } from '@/content/exams';
import { dojoById } from '@/content/dojos';
import { CHANGED_IN } from './modes';

export const SAVE_KEY = 'kiai-save';
/** Set to 'no' when the player declined saving, so we don't ask again. */
export const CONSENT_KEY = 'kiai-consent';

/** 'yes' = save progress, 'no' = declined, null = not asked yet. */
export type Consent = 'yes' | 'no' | null;

export interface Save {
  v: 2;
  /** Index into BELTS: the highest belt earned. */
  belt: number;
  /** Best stars (0 = not passed) and best score per exam, indexed like EXAMS. */
  stars: number[];
  examBest: number[];
  /** Mastery marks per exam: MARK bits (hanko, gold stripe), indexed like EXAMS. */
  marks: number[];
  kumiteBest: number;
  /** The SIM_VERSION Kumite was at when that best was set: a best from an
   *  easier Kumite (before CHANGED_IN.kumite) is reset. */
  kumiteV: number;
  daily: { date: string; best: number };
  exams: number; // exam attempts, for analytics and pacing hints
  /** The dojo the player picked for their free trial (DOJOS id), if any. */
  dojo: string | null;
  /** Until they pick: the dojo drawn at random for them (DOJOS id), so it
   *  stays the same across visits once they save progress. */
  drawn: string | null;
  /** Training streak (meta/streak.ts): the last seven days the daily exam was
   *  played, ascending, and the current, best and total count of such days. */
  train: { days: string[]; streak: number; best: number; total: number };
  /** What the in-fight coach no longer needs to show (app/coach.ts COACHED bits). */
  coach: number;
}

export function freshSave(): Save {
  return {
    v: 2,
    belt: 0,
    stars: [],
    examBest: [],
    marks: [],
    kumiteBest: 0,
    kumiteV: CHANGED_IN.kumite,
    daily: { date: '', best: 0 },
    exams: 0,
    dojo: null,
    drawn: null,
    train: { days: [], streak: 0, best: 0, total: 0 },
    coach: 0,
  };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const MAX = Number.MAX_SAFE_INTEGER;
const int = (x: unknown, min: number, max: number): number =>
  typeof x === 'number' && Number.isFinite(x) ? Math.min(max, Math.max(min, Math.floor(x))) : min;

export function normalizeSave(raw: unknown): Save {
  const s = freshSave();
  if (typeof raw !== 'object' || raw === null) return s;
  const r = migrate(raw as Record<string, unknown>);
  s.belt = int(r.belt, 0, BELTS.length - 1);
  const arr = (x: unknown, max: number): number[] =>
    Array.isArray(x) ? x.slice(0, BELTS.length - 1).map((v) => int(v, 0, max)) : [];
  s.stars = arr(r.stars, 3);
  s.examBest = arr(r.examBest, Number.MAX_SAFE_INTEGER);
  s.marks = arr(r.marks, 3);
  // Kumite got harder since an old best was set: it starts fresh
  const fresh = int(r.kumiteV, 0, Number.MAX_SAFE_INTEGER) >= CHANGED_IN.kumite;
  s.kumiteBest = fresh ? int(r.kumiteBest, 0, Number.MAX_SAFE_INTEGER) : 0;
  const d = r.daily as Record<string, unknown> | undefined;
  if (d && typeof d.date === 'string' && ISO_DATE.test(d.date)) {
    s.daily = { date: d.date, best: int(d.best, 0, Number.MAX_SAFE_INTEGER) };
  }
  s.exams = int(r.exams, 0, Number.MAX_SAFE_INTEGER);
  // Unknown ids (a dojo removed from the list) fall back to "not chosen"
  s.dojo = dojoById(typeof r.dojo === 'string' ? r.dojo : null)?.id ?? null;
  s.drawn = dojoById(typeof r.drawn === 'string' ? r.drawn : null)?.id ?? null;
  const t = r.train as Record<string, unknown> | undefined;
  if (t && typeof t === 'object') {
    const days = Array.isArray(t.days)
      ? [...new Set(t.days.filter((x): x is string => typeof x === 'string' && ISO_DATE.test(x)))]
          .sort()
          .slice(-7)
      : [];
    const streak = days.length ? Math.max(1, int(t.streak, 0, MAX)) : 0;
    s.train = {
      days,
      streak,
      best: Math.max(streak, int(t.best, 0, MAX)),
      total: Math.max(days.length, int(t.total, 0, MAX)),
    };
  } else if (s.daily.date) {
    // Saved before streaks: the last daily exam played starts one
    s.train = { days: [s.daily.date], streak: 1, best: 1, total: 1 };
  }
  s.coach = int(r.coach, 0, 0xff);
  return s;
}

/**
 * v1 had no red belt (white → yellow → … → black, 7 exams). v2 inserts red
 * after white, so every earned belt and exam moves up one; anyone who had
 * yellow has, by definition, passed the new red exam too.
 */
function migrate(r: Record<string, unknown>): Record<string, unknown> {
  if (r.v !== 1) return r;
  const shift = (x: unknown): unknown =>
    Array.isArray(x) && x.length > 0 ? [x[0] as unknown, ...(x as unknown[])] : x;
  const belt = typeof r.belt === 'number' && r.belt > 0 ? r.belt + 1 : r.belt;
  return { ...r, v: 2, belt, stars: shift(r.stars), examBest: shift(r.examBest) };
}

export interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStore(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadSave(store: Store | null = defaultStore()): Save {
  try {
    const raw = store?.getItem(SAVE_KEY);
    return normalizeSave(raw ? JSON.parse(raw) : null);
  } catch {
    return freshSave();
  }
}

export function persist(save: Save, store: Store | null = defaultStore()): void {
  try {
    store?.setItem(SAVE_KEY, JSON.stringify(save));
  } catch {
    // Private mode / quota: progress just isn't kept
  }
}

/** An existing save means the player already keeps progress here (and every
 *  player from before the consent question keeps theirs). */
export function loadConsent(store: Store | null = defaultStore()): Consent {
  try {
    if (store?.getItem(SAVE_KEY)) return 'yes';
    return store?.getItem(CONSENT_KEY) === 'no' ? 'no' : null;
  } catch {
    return null;
  }
}

/** Yes writes the current progress; no deletes any stored progress. */
export function setConsent(
  yes: boolean,
  save: Save,
  store: Store | null = defaultStore(),
): Consent {
  try {
    if (yes) {
      store?.removeItem(CONSENT_KEY);
      store?.setItem(SAVE_KEY, JSON.stringify(save));
    } else {
      store?.removeItem(SAVE_KEY);
      store?.setItem(CONSENT_KEY, 'no');
    }
  } catch {
    // Private mode / quota: the choice holds for this session only
  }
  return yes ? 'yes' : 'no';
}

export interface ExamOutcome {
  exam: number;
  passed: boolean;
  score: number;
  stars: number;
  /** Mastery marks earned in this run (MARK bits). */
  marks?: number;
}

/** Fold an exam result in: whether it was a promotion to a new belt, a new
 *  best score, and which marks are new. */
export function recordExam(
  save: Save,
  o: ExamOutcome,
): { promoted: boolean; newBest: boolean; newMarks: number } {
  save.exams++;
  const newBest = o.score > (save.examBest[o.exam] ?? 0);
  if (!o.passed) return { promoted: false, newBest: false, newMarks: 0 };
  if (newBest) save.examBest[o.exam] = o.score;
  save.stars[o.exam] = Math.max(save.stars[o.exam] ?? 0, o.stars);
  const had = save.marks[o.exam] ?? 0;
  save.marks[o.exam] = had | (o.marks ?? 0);
  // Fill holes so the arrays stay dense for JSON
  for (let i = 0; i < o.exam; i++) {
    save.stars[i] ??= 0;
    save.examBest[i] ??= 0;
    save.marks[i] ??= 0;
  }
  const promoted = o.exam + 1 > save.belt;
  if (promoted) save.belt = o.exam + 1;
  return { promoted, newBest, newMarks: (o.marks ?? 0) & ~had };
}

/** Local calendar date, so "today" matches the player's day. */
export function todayISO(now = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${m}-${d}`;
}

export function recordDaily(save: Save, date: string, score: number): boolean {
  if (save.daily.date !== date) save.daily = { date, best: 0 };
  const newBest = score > save.daily.best;
  if (newBest) save.daily.best = score;
  return newBest;
}

export function recordKumite(save: Save, score: number): boolean {
  const newBest = score > save.kumiteBest;
  if (newBest) save.kumiteBest = score;
  return newBest;
}
